import { useMutation, useQueryClient } from "@tanstack/react-query";
import { keycloakApi } from "@/integrations/keycloak/client";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useUnifiedAuth } from "@/hooks/useUnifiedAuth";
import { useAnalytics } from "@/hooks/useAnalytics";
import { hashEmail } from "@/lib/analytics";

interface DeleteUserParams {
  userId: string;
  email: string;
  role: string | null;
  generatorLinks?: Array<{ generator_id: number }>;
}

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { keycloakToken } = useUnifiedAuth();
  const { trackButtonClick } = useAnalytics();

  return useMutation({
    mutationFn: async ({ userId, email }: DeleteUserParams) => {
      if (!userId) {
        throw new Error("User ID é obrigatório");
      }

      if (!email) {
        throw new Error("Email é obrigatório");
      }

      // Converter userId para número (a API Keycloak espera um número)
      const userIdNumber = parseInt(userId, 10);
      if (isNaN(userIdNumber)) {
        throw new Error("ID do usuário inválido");
      }

      // 1. Deletar do Keycloak primeiro
      await keycloakApi.deleteUser(userIdNumber);

      // 2. Depois deletar do Supabase via Edge Function
      if (keycloakToken) {
        try {
          const { error } = await supabase.functions.invoke("delete-user", {
            body: { email },
            headers: {
              "x-keycloak-token": keycloakToken,
            },
          });

          if (error) {
            console.warn("Aviso ao deletar usuário do Supabase:", error);
            // Continuar mesmo que haja erro no Supabase
          }
        } catch (err) {
          console.warn("Erro ao chamar função delete-user:", err);
          // Continuar mesmo que haja erro
        }
      }
    },
    onSuccess: async (_, variables) => {
      const targetUserEmailHash = await hashEmail(variables.email);
      const generatorLinks = variables.generatorLinks ?? [];
      trackButtonClick({
        buttonLabel: "usuarios_deletar_usuario",
        target_user_id: variables.userId,
        target_user_role: variables.role ?? "",
        target_user_email_hash: targetUserEmailHash,
        generator_ids_affected: generatorLinks.map((g) => g.generator_id).join(","),
        generator_count_affected: generatorLinks.length,
      });

      // Invalidar queries relacionadas a usuários
      queryClient.invalidateQueries({ queryKey: ["users-with-keycloak"] });
      queryClient.invalidateQueries({ queryKey: ["users-with-permissions"] });
      toast({
        title: "Usuário removido",
        description: "O usuário foi removido com sucesso.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao remover usuário",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};
